import * as THREE from 'three';

export default class Tree {
    constructor(scene, physics, position = { x: 0, z: 0 }, type = 'conifer') {
        this.scene = scene;
        this.physics = physics;
        this.position = position;
        this.type = type; // 'conifer' or 'broadleaf'
        
        // Tree meshes
        this.trunk = null;
        this.foliage = [];
        this.treeGroup = null;
        
        // Physics body
        this.collider = null;
        
        // Tree properties (will be randomized during creation)
        this.height = 0;
        this.trunkRadius = 0;
        this.foliageRadius = 0;
        
        // Create the tree based on type
        this.create();
    }
    
    create() {
        // Create the appropriate tree type
        if (this.type === 'conifer') {
            this.createConiferTree();
        } else {
            this.createBroadleafTree();
        }
    }
    
    createConiferTree() {
        // Generate random height for variation
        this.height = 6 + Math.random() * 6;
        
        // Create tree trunk (cylinder)
        const trunkGeometry = new THREE.CylinderGeometry(0.3, 0.4, this.height * 0.8, 5);
        const trunkMaterial = new THREE.MeshStandardMaterial({
            color: 0x8B4513,  // Brown
            roughness: 0.9,
            metalness: 0.1,
            flatShading: true
        });
        
        this.trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(this.position.x, this.position.z) : 0;
        this.trunk.position.set(this.position.x, terrainY + this.height * 0.2, this.position.z);
        this.trunk.castShadow = true;
        this.trunk.receiveShadow = true;
        this.scene.add(this.trunk);

        // Create foliage (cone)
        const foliageGeometry = new THREE.ConeGeometry(this.height * 0.6, this.height * 0.9, 6);

        // Choose a random green shade for variation
        const hue = 0.25 + (Math.random() * 0.15); // Green hue
        const saturation = 0.5 + (Math.random() * 0.3);
        const lightness = 0.25 + (Math.random() * 0.2);
        const color = new THREE.Color().setHSL(hue, saturation, lightness);

        const foliageMaterial = new THREE.MeshStandardMaterial({
            color: color,
            roughness: 1.0,
            metalness: 0.0,
            flatShading: true
        });

        const foliage = new THREE.Mesh(foliageGeometry, foliageMaterial);
        foliage.position.set(this.position.x, terrainY + this.height * 0.8, this.position.z);
        foliage.castShadow = true;
        foliage.receiveShadow = true;
        
        // Random rotation for variation
        foliage.rotation.y = Math.random() * Math.PI;
        
        this.scene.add(foliage);
        this.foliage.push(foliage);
        
        // Add physics collider for the tree
        this.createPhysicsCollider();
    }
    
    createBroadleafTree() {
        // Generate random height for variation
        this.height = 5.5 + Math.random() * 5.0;
        
        // Create tree trunk (cylinder)
        const trunkGeometry = new THREE.CylinderGeometry(0.25, 0.35, this.height * 0.5, 5);
        const trunkMaterial = new THREE.MeshStandardMaterial({
            color: 0x5D4037, // Darker brown
            roughness: 0.9,
            metalness: 0.1,
            flatShading: true
        });
        
        this.trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
        const blHeightMap = this.scene?.userData?.terrainHeightMap;
        const blTerrainY = blHeightMap ? blHeightMap.getHeight(this.position.x, this.position.z) : 0;
        this.trunk.position.set(this.position.x, blTerrainY + this.height * 0.25, this.position.z);
        this.trunk.castShadow = true;
        this.trunk.receiveShadow = true;

        // Add slight random tilt
        this.trunk.rotation.z = (Math.random() - 0.5) * 0.2;
        this.trunk.rotation.x = (Math.random() - 0.5) * 0.2;

        this.scene.add(this.trunk);

        // Create a tree group to add foliage
        this.treeGroup = new THREE.Group();
        this.treeGroup.position.set(this.position.x, blTerrainY, this.position.z);
        this.scene.add(this.treeGroup);
        
        // Create multiple foliage clusters (spheres)
        const foliageClusters = 3 + Math.floor(Math.random() * 3);
        
        // Choose a green base color for the whole tree
        const hue = 0.25 + (Math.random() * 0.15); // Green hue
        const saturation = 0.4 + (Math.random() * 0.3);
        const lightness = 0.25 + (Math.random() * 0.2);
        const baseColor = new THREE.Color().setHSL(hue, saturation, lightness);
        
        for (let i = 0; i < foliageClusters; i++) {
            // Calculate position within the crown of the tree
            const angle = Math.random() * Math.PI * 2;
            const radiusXZ = 0.1 + Math.random() * 0.5;
            const offsetX = Math.cos(angle) * radiusXZ;
            const offsetZ = Math.sin(angle) * radiusXZ;
            
            // Higher clusters are higher up and smaller
            const verticalPosition = this.height * 0.6 + (i * this.height * 0.15);
            
            // Adjust color slightly for each cluster
            const clusterColor = baseColor.clone();
            const hueShift = (Math.random() - 0.5) * 0.05;
            const lightnessShift = (Math.random() - 0.5) * 0.1;
            clusterColor.offsetHSL(hueShift, 0, lightnessShift);
            
            // Create foliage geometry
            const foliageSize = this.height * (0.4 - i * 0.05);
            const foliageGeometry = new THREE.IcosahedronGeometry(foliageSize, 0); // Low-poly sphere
            const foliageMaterial = new THREE.MeshStandardMaterial({
                color: clusterColor,
                roughness: 1.0,
                metalness: 0.0,
                flatShading: true
            });
            
            const foliage = new THREE.Mesh(foliageGeometry, foliageMaterial);
            foliage.position.set(offsetX, verticalPosition, offsetZ);
            foliage.castShadow = true;
            foliage.receiveShadow = true;
            
            // Random rotation for variation
            foliage.rotation.y = Math.random() * Math.PI;
            
            this.treeGroup.add(foliage);
            this.foliage.push(foliage);
        }
        
        // Add physics collider for the tree
        this.createPhysicsCollider();
    }
    
    createPhysicsCollider() {
        if (!this.physics || !this.physics.isInitialized) return;
        
        if (this.type === 'conifer') {
            // Create a single box collider that encompasses both trunk and foliage
            this.trunkRadius = 0.4; // Slightly larger than visual model
            const colliderHalfExtents = new THREE.Vector3(
                this.trunkRadius * 1.2, // Make the collider a bit wider than the trunk
                this.height * 0.5,     // Height of the collider (half the tree's height)
                this.trunkRadius * 1.2  // Make the collider a bit wider than the trunk
            );
            
            const coniferHeightMap = this.scene?.userData?.terrainHeightMap;
            const coniferTerrainY = coniferHeightMap ? coniferHeightMap.getHeight(this.position.x, this.position.z) : 0;
            const colliderPosition = new THREE.Vector3(
                this.position.x,
                coniferTerrainY + this.height * 0.5,
                this.position.z
            );
            
            // Create static box collider
            this.collider = this.physics.createStaticBox(
                colliderPosition,
                colliderHalfExtents,
                0.8 // Higher friction for trees
            );
            
            // Store reference to meshes for collision visualization if needed
            this.collider.visualMeshes = [this.trunk, ...this.foliage];
        } else {
            // For broadleaf tree
            // Calculate the effective radius of the tree for collision
            this.trunkRadius = 0.35; // Slightly larger than visual trunk
            this.foliageRadius = this.height * 0.6; // Approximation of foliage width
            
            // Create a single box collider that encompasses the whole tree
            const colliderHalfExtents = new THREE.Vector3(
                this.foliageRadius * 0.7, // Make the collider a bit smaller than visual foliage
                this.height * 0.6,       // Height of the collider (partial tree height)
                this.foliageRadius * 0.7  // Make the collider a bit smaller than visual foliage
            );
            
            const blHeightMap2 = this.scene?.userData?.terrainHeightMap;
            const blTerrainY2 = blHeightMap2 ? blHeightMap2.getHeight(this.position.x, this.position.z) : 0;
            const colliderPosition = new THREE.Vector3(
                this.position.x,
                blTerrainY2 + this.height * 0.5,
                this.position.z
            );
            
            // Create static box collider
            this.collider = this.physics.createStaticBox(
                colliderPosition,
                colliderHalfExtents,
                0.7 // High friction for broadleaf trees
            );
            
            // Store reference to all meshes for collision visualization if needed
            this.collider.visualMeshes = [this.trunk, ...this.foliage];
        }
        
        // Register this tree as an obstacle
        this.registerAsObstacle();
    }
    
    registerAsObstacle() {
        // Add tree to obstacles list if game has one
        if (this.scene.userData && this.scene.userData.obstacles) {
            const effectiveRadius = this.type === 'conifer' ? 
                this.trunkRadius * 1.5 : 
                this.foliageRadius * 0.9;
                
            this.scene.userData.obstacles.push({
                position: { 
                    x: this.position.x, 
                    y: this.height * 0.5, 
                    z: this.position.z 
                },
                radius: effectiveRadius
            });
        }
    }
    
    // For removing the tree from the scene if needed
    remove() {
        // Remove meshes from scene
        if (this.trunk) {
            this.scene.remove(this.trunk);
        }
        
        this.foliage.forEach(foliage => {
            if (this.treeGroup) {
                this.treeGroup.remove(foliage);
            } else {
                this.scene.remove(foliage);
            }
        });
        
        if (this.treeGroup) {
            this.scene.remove(this.treeGroup);
        }
        
        // Remove from physics world if collider exists
        if (this.physics && this.physics.bodies && this.collider) {
            const index = this.physics.bodies.indexOf(this.collider);
            if (index !== -1) {
                this.physics.bodies.splice(index, 1);
            }
            
            const colliderIndex = this.physics.colliders.indexOf(this.collider);
            if (colliderIndex !== -1) {
                this.physics.colliders.splice(colliderIndex, 1);
            }
        }
    }
} 